import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle, ArrowLeft, Calendar, Users, School,
  Mail, Phone, MessageSquare, Sparkles
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GRADE_BANDS = [
  { id: "K-2", label: "K-2", desc: "Kindergarten through Grade 2" },
  { id: "3-5", label: "3-5", desc: "Grades 3 through 5" },
  { id: "6-8", label: "6-8", desc: "Grades 6 through 8" },
  { id: "9-12", label: "9-12", desc: "Grades 9 through 12" },
];

const RequestDemo = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    title: "",
    organization: "",
    email: "",
    phone: "",
    grade_bands: [],
    estimated_ell_students: "",
    message: "",
    preferred_date: "",
  });

  const toggleGradeBand = (id) => {
    setForm((prev) => ({
      ...prev,
      grade_bands: prev.grade_bands.includes(id)
        ? prev.grade_bands.filter((g) => g !== id)
        : [...prev.grade_bands, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim() || !form.title.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="demo-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Thank You!
          </h1>
          <p className="text-gray-600 mb-2 text-lg">
            We received your demo request for <strong>{form.organization}</strong>.
          </p>
          <p className="text-gray-500 mb-8">
            Our team will reach out within 1-2 business days to schedule your personalized demo.
          </p>
          <div className="space-y-3">
            <Link to="/features">
              <Button className="w-full btn-sharp-primary h-12" data-testid="explore-features-btn">
                <Sparkles size={18} className="mr-2" />
                Explore Features While You Wait
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full btn-sharp h-12 mt-2" data-testid="back-home-btn">
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-[#002FA7] text-white py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Request a Demo
          </h1>
          <p className="text-xs tracking-widest uppercase text-blue-200/60 font-medium mb-3" style={{ letterSpacing: '0.15em' }}>
            Multilingual Oral &amp; Written Student Assessment for Instructional Clarity
          </p>
          <p className="text-lg text-blue-100 max-w-xl mx-auto">
            See how MOSAIC helps your school or district assess and support multilingual learners with language-proficiency assessments.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <form onSubmit={handleSubmit} className="space-y-8" data-testid="demo-request-form">
          {/* Contact Info */}
          <div className="bg-white border-2 border-gray-200 p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Mail size={20} className="text-[#002FA7]" />
              Your Information
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold mb-1 block">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="title" className="text-sm font-semibold mb-1 block">Title / Role *</Label>
                <Input
                  id="title"
                  placeholder="ELL Coordinator, Principal, etc."
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-title"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold mb-1 block">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane.smith@school.nyc"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold mb-1 block">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(212) 555-0100"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-phone"
                />
              </div>
            </div>
          </div>

          {/* Organization */}
          <div className="bg-white border-2 border-gray-200 p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <School size={20} className="text-[#002FA7]" />
              School / District
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="org" className="text-sm font-semibold mb-1 block">School or District Name *</Label>
                <Input
                  id="org"
                  placeholder="P.S. 124 Yung Wing, NYC District 2, etc."
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-organization"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-2 block">Grade Bands of Interest</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="demo-grade-bands">
                  {GRADE_BANDS.map((band) => (
                    <button
                      key={band.id}
                      type="button"
                      onClick={() => toggleGradeBand(band.id)}
                      className={`p-3 border-2 text-center transition-all ${
                        form.grade_bands.includes(band.id)
                          ? "border-[#002FA7] bg-blue-50 text-[#002FA7]"
                          : "border-gray-200 hover:border-gray-300 text-gray-600"
                      }`}
                      data-testid={`grade-band-${band.id}`}
                    >
                      <div className="font-bold text-lg">{band.label}</div>
                      <div className="text-xs mt-0.5 opacity-70">{band.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="ell-count" className="text-sm font-semibold mb-1 block">
                  Estimated Number of ELL Students <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="ell-count"
                  placeholder="e.g., 150, 500+, Not sure"
                  value={form.estimated_ell_students}
                  onChange={(e) => setForm({ ...form, estimated_ell_students: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-ell-students"
                />
              </div>
            </div>
          </div>

          {/* Schedule & Message */}
          <div className="bg-white border-2 border-gray-200 p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Calendar size={20} className="text-[#002FA7]" />
              Schedule & Message
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="preferred-date" className="text-sm font-semibold mb-1 block">
                  Preferred Demo Date <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="preferred-date"
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                  data-testid="demo-date"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-sm font-semibold mb-1 block">
                  Questions or Specific Interests <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your needs — e.g., 'We're transitioning from NYSESLAT and need a platform that supports K-5 with adaptive assessments...'"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="min-h-[100px] border-2 border-gray-300 focus:border-[#002FA7] rounded-none resize-none"
                  data-testid="demo-message"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg btn-sharp-primary"
            data-testid="demo-submit-btn"
          >
            {loading ? (
              <><div className="loading-spinner w-5 h-5 mr-2 border-white border-t-transparent"></div> Submitting...</>
            ) : (
              <><Users size={20} className="mr-2" /> Request Your Demo</>
            )}
          </Button>

          <p className="text-center text-xs text-gray-400">
            We'll respond within 1-2 business days. Your information is kept confidential.
          </p>
        </form>
      </div>
    </div>
  );
};

export default RequestDemo;
